<template>
  <div v-if="showMatchingDatesLayer" class="mt-6">
    <div v-if="calendarLoading" class="text-center py-4">
      {{ t('general.loading') }}
    </div>
    <div v-else class="space-y-3">
      <div
        v-for="(date, index) in matchingDatesWithDateRequestSelections"
        :key="date.id"
        class="flex items-start p-3 rounded-lg cursor-pointer hover:bg-gray-50"
        :class="{
          'bg-teal-50 border-teal-200 hover:bg-teal-50':
            selectedAvailableDates.some((selected) => selected.id === date.id),
        }"
        @click="toggleDateSelection(date)"
      >
        <input
          :id="`date-${date.id}`"
          type="checkbox"
          :checked="
            selectedAvailableDates.some((selected) => selected.id === date.id)
          "
          class="mr-3 mt-1.5 accent-teal-600"
          @click.stop
          @change="toggleDateSelection(date)"
        />
        <div class="flex-1">
          <div class="font-medium">
            {{ eventGetters.getTitle(eventData) }}
          </div>
          <div class="text-sm text-gray-600">
            {{ dayjs(date.start).format('DD.MM.YYYY HH:mm') }}
          </div>
          <div class="text-sm text-gray-500">
            {{ form.numberOfTickets }}
            {{ t('product.dateRequest.people') }}
          </div>
        </div>
        <div class="ml-3 h-full flex flex-col justify-between">
          <span
            class="px-2 py-1 mb-3 text-xs rounded-md bg-teal-50 text-teal-600 border-teal-600 border"
            :class="{
              'bg-teal-50 text-teal-600 border-teal-600 border':
                selectedAvailableDates.some(
                  (selected) => selected.id === date.id
                ),
            }"
          >
            {{
              date.dateType === 'PUBLIC'
                ? t('product.show.datePickerCaptions.publicDateLabel')
                : t('product.show.datePickerCaptions.privateDateTypeLabel')
            }}
          </span>
          <div class="text-right font-semibold mt-1">
            €{{ (Number(date.product.price.amount) / 100).toFixed(2) }}
          </div>
        </div>
        <div
          v-if="index < matchingDatesWithDateRequestSelections.length - 1"
          class="border-b border-gray-200 mt-3"
        ></div>
      </div>
    </div>
    <div class="mt-4 pt-4 border-t">
      <Button
        class="w-full py-3"
        :disabled="selectedAvailableDates.length === 0"
        @click="addSelectedDatesToCart"
      >
        {{ t('product.dateRequest.addToCart') }}
      </Button>
      <Button class="w-full mt-4" variant="text" @click="$emit('close')">
        {{ t('product.dateRequest.requestOtherDates') }}
      </Button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import dayjs from 'dayjs'
import type { EventCalendarDayMap, EventDate } from '~/composables/types/event'
import parseEventDateToCartItem from '~/utils/parseEventDateToCartItem'

// Use EventDate directly instead of custom interface

interface Props {
  eventData: any
  form: {
    dates: string[]
    numberOfTickets: number
  }
  showMatchingDatesLayer: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  addToCart: [dates: EventDate[]]
  close: []
  'availability-changed': [hasAvailableDates: boolean]
}>()

const { t } = useI18n()

const availableDates = ref<EventDate[]>([])
const selectedAvailableDates = ref<EventDate[]>([])
const calendarLoading = ref(false)

// Computed property to filter dates that match user's date request selections
const matchingDatesWithDateRequestSelections = computed(() => {
  if (!props.form.dates || props.form.dates.length === 0) {
    return []
  }

  return availableDates.value.filter((availableDate) => {
    // Check if any of the user's selected dates match this available date
    return props.form.dates.some((userDate) => {
      if (!userDate) return false

      const userDateObj = dayjs(userDate)
      const availableDateObj = dayjs(availableDate.start) // Use start from EventDate

      // Check if dates match (same day)
      if (!userDateObj.isSame(availableDateObj, 'day')) {
        return false
      }

      // Check if times are within 1 hour window
      const timeDiffMinutes = Math.abs(
        userDateObj.diff(availableDateObj, 'minutes')
      )
      return timeDiffMinutes <= 60 // Within 1 hour
    })
  })
})

// Computed property to determine if we should show the sidebar
const shouldShowSidebar = computed(() => {
  return matchingDatesWithDateRequestSelections.value.length > 0
})

// Emit to parent when availability changes
watch(
  shouldShowSidebar,
  (newValue) => {
    emit('availability-changed', newValue)
  },
  { immediate: true }
)

// Auto-select if there's only one matching date
watch(
  matchingDatesWithDateRequestSelections,
  (newMatchingDates) => {
    selectedAvailableDates.value = [newMatchingDates[0]]
  },
  { immediate: true }
)

const fetchAvailableDates = async () => {
  if (!props.form.dates || props.form.dates.length === 0) {
    availableDates.value = []
    return
  }

  calendarLoading.value = true

  try {
    // Extract months from selected dates
    const months = [
      ...new Set(
        props.form.dates
          .filter((date) => Boolean(date))
          .map((date) => dayjs(date).format('YYYY-MM'))
      ),
    ]

    const allAvailableDates: EventDate[] = []

    // Fetch calendar data for each month
    for (const month of months) {
      const response = await kftApiRequest<EventCalendarDayMap>(
        `/v1/store/events/${eventGetters.getId(props.eventData)}/calendar`,
        {
          method: 'GET',
          query: { month },
        }
      )

      if (response) {
        // Process the calendar data to find available dates with sufficient capacity
        Object.entries(response).forEach(([_date, dayData]) => {
          if ((dayData as any).dates && Array.isArray((dayData as any).dates)) {
            ;(dayData as any).dates.forEach((slot: EventDate) => {
              if (
                slot.availableTicketsQuantity >=
                  Number(props.form.numberOfTickets) &&
                slot.status === 'OPEN'
              ) {
                allAvailableDates.push(slot)
              }
            })
          }
        })
      }
    }

    availableDates.value = allAvailableDates
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching available dates:', error)
    availableDates.value = []
  } finally {
    calendarLoading.value = false
  }
}

const addSelectedDatesToCart = () => {
  const { addItem } = useCart('general-instance')
  const { toggleCartSidebar } = useUiState()

  selectedAvailableDates.value.forEach((selectedDate) => {
    const cartItem = parseEventDateToCartItem(
      props.eventData,
      Number(props.form.numberOfTickets),
      ref(selectedDate), // Use EventDate directly
      ref([]), // No add-ons for date requests
      selectedDate.dateType === 'PRIVATE', // isPrivate based on dateType
      '' // queryId
    )

    addItem(cartItem)
  })

  // Open cart sidebar after adding items
  toggleCartSidebar()

  // Emit to parent to handle closing
  emit('addToCart', selectedAvailableDates.value)
}

const toggleDateSelection = (date: EventDate) => {
  const index = selectedAvailableDates.value.findIndex(
    (selected) => selected.id === date.id
  )
  if (index > -1) {
    selectedAvailableDates.value.splice(index, 1)
  } else {
    selectedAvailableDates.value.push(date)
  }
}

// Watch for date changes to fetch available dates
watch(
  () => props.form.dates,
  async (newDates) => {
    if (newDates && newDates.some((date) => date !== '')) {
      await fetchAvailableDates()
    } else {
      availableDates.value = []
      selectedAvailableDates.value = []
    }
  },
  { deep: true }
)

// Expose computed properties for parent component
defineExpose({
  showAvailableDates: shouldShowSidebar,
  selectedAvailableDates: readonly(selectedAvailableDates),
  calendarLoading: readonly(calendarLoading),
  availableDates: readonly(availableDates),
  addSelectedDatesToCart,
})
</script>
